import { Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Chip from '@/components/General/Chip';
import {
    getAttentionReasonLabel,
    getAttentionReasonIcon,
    getAttentionReasonSnooze,
    formatSnoozeUntil,
} from '@/utils/constants/followUp';

/**
 * AttentionReasonChips — reason chips shared by the Overview Needs Attention
 * card, the monitored People list, and My Follow Up rows.
 *
 * Accepts reason objects ({reason, audienceID?, audienceName?,
 * firstDetectedAt?, snoozed?}) or bare reason strings. Snoozed reasons stay
 * VISIBLE but are rendered suppressed with "Snoozed until <date>" — a snooze
 * is never a hidden "healthy" switch (spec §11.6). Each reason is its own
 * chip so snoozing an attendance reason can't visually erase contact flags.
 */
const AttentionReasonChips = ({ reasons = [], testIDPrefix = 'attention-reason' }) => {
    if (!reasons?.length) return null;

    return (
        <View style={styles.wrap}>
            {reasons.map((entry, index) => {
                const reason = typeof entry === 'string' ? { reason: entry } : entry;
                if (!reason?.reason) return null;
                const snooze = getAttentionReasonSnooze(reason);
                const key = `${reason.reason}-${reason.audienceID ?? 'org'}-${index}`;
                const snoozedText = formatSnoozeUntil(snooze?.until)
                    ? `Snoozed until ${formatSnoozeUntil(snooze.until)}`
                    : 'Snoozed';

                return (
                    <View key={key} style={styles.item}>
                        <Chip
                            testID={snooze ? `${testIDPrefix}-snoozed-${reason.reason}` : `${testIDPrefix}-${reason.reason}`}
                            icon={snooze ? 'moon-outline' : getAttentionReasonIcon(reason.reason)}
                            colorVariant={snooze ? 'textSecondary' : 'warning'}
                            style={snooze && styles.snoozedChip}
                            textStyle={snooze && styles.snoozedText}
                        >
                            {getAttentionReasonLabel(reason.reason) + (reason.audienceName ? ` · ${reason.audienceName}` : '')}
                        </Chip>
                        {!!snooze && (
                            <Text style={styles.snoozedLabel}>{snoozedText}</Text>
                        )}
                    </View>
                );
            })}
        </View>
    );
};

const styles = StyleSheet.create(({ colors }) => ({
    wrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    item: { gap: 2 },
    snoozedChip: { opacity: 0.6 },
    snoozedText: { fontStyle: 'italic' },
    snoozedLabel: { fontSize: 10, color: colors.textTertiary, fontStyle: 'italic', paddingLeft: 4 },
}));

export default AttentionReasonChips;
